import { getUserData } from "./API";
import StudentInformationService from "./StudentInformationService";

/**
 * FTUEService class provides utilities for managing and retrieving first time user experience (FTUE) statuses.
 */
class FTUEService {
  /**
   * Retrieves an array of FTUE IDs where the status is true.
   * This represents FTUEs that have been completed.
   *
   * @returns {string[]} - Array of FTUE IDs with a status of 'true'.
   */
  static getCompletedFTUEs() {
    // Access the user data object
    const userData = getUserData();

    // Check if ftueStatuses exists and is an array, otherwise return an empty array
    if (!Array.isArray(userData.ftueStatuses)) {
      return [];
    }

    // Filter FTUE statuses where status is true and map to extract IDs
    return userData.ftueStatuses
      .filter((ftue) => ftue.status === true)
      .map((ftue) => ftue.id);
  }

  /**
   * Asynchronously creates or updates an FTUE status based on the provided FTUE ID and status.
   * Saves the updated ftueStatuses array to the backend service.
   *
   * @param {string} id - The ID of the FTUE to create or update.
   * @param {boolean} status - The status to set for the FTUE (true for completed, false for not completed).
   * @returns {Promise<void>} - Resolves when the update is complete.
   */
  static async setFTUEStatus(id, status) {
    const userData = getUserData();

    if (!Array.isArray(userData.ftueStatuses)) {
      userData.ftueStatuses = [];
    }

    const ftueIndex = userData.ftueStatuses.findIndex((ftue) => ftue.id === id);

    if (ftueIndex !== -1) {
      // Update the status if the FTUE entry already exists
      userData.ftueStatuses[ftueIndex].status = status;
    } else {
      // Add a new entry if the FTUE entry does not exist
      userData.ftueStatuses.push({ id, status });
    }

    // Save the updated user data to backend
    await StudentInformationService.setFTUEStatuses(
      userData.userId,
      userData.ftueStatuses
    );
  }

  /**
   * Checks if a specific FTUE has been completed.
   *
   * @param {string} ftueId - The ID of the FTUE to check.
   * @returns {boolean} - Returns true if the FTUE has a status of 'true', false otherwise.
   */
  static isFTUECompleted(ftueId) {
    const userData = getUserData();

    if (!Array.isArray(userData.ftueStatuses)) {
      return false;
    }

    const ftue = userData.ftueStatuses.find((ftue) => ftue.id === ftueId);
    return ftue ? ftue.status === true : false;
  }
}

export default FTUEService;
